/*
 * Quick view: a tap on a sold-out size turns the quick view itself into STOQ's
 * back-in-stock sign-up for that exact variant (one layer, content swaps), with
 * a Back control to return to the product.
 *
 * The form is STOQ's own (same copy, consent text and waitlist as on product
 * pages), rendered through the STOQ Storefront SDK's openInlineForm, which works
 * inside any element. The SDK is loaded when a quick view with a sold-out size
 * opens (or on the first tap), never on pages without one.
 * Fallback: if the SDK cannot load or render, STOQ's regular modal opens over
 * the quick view (window._RestockRocket.openModal), so the shopper always
 * reaches the sign-up and the quick view is never closed on her. Without STOQ
 * at all the tap stays inert, as before.
 */
(function () {
  if (window.__quickViewNotifyInit) return;
  window.__quickViewNotifyInit = true;

  var script = document.currentScript;
  var strings = {
    back: (script && script.getAttribute('data-label-back')) || 'Back',
    loading: (script && script.getAttribute('data-label-loading')) || 'Loading...',
    invalidEmail: (script && script.getAttribute('data-label-invalid-email')) || 'Please enter a valid email address'
  };

  var SDK_SRC = 'https://cdn.jsdelivr.net/npm/@artossoftware/stoq-sdk@0/dist/stoq.min.js';
  var SDK_TIMEOUT = 6000;
  var productCache = {};
  var productData = {};
  var sdkClient = null;

  function embed() {
    return window._RestockRocket && typeof window._RestockRocket.openModal === 'function'
      ? window._RestockRocket
      : null;
  }

  function productJsUrl(url) {
    var path = (url || '').split('?')[0].split('#')[0];
    return path ? path + '.js' : '';
  }

  function loadProduct(url) {
    var key = productJsUrl(url);
    if (!key) return Promise.reject(new Error('no product url'));
    if (!productCache[key]) {
      productCache[key] = fetch(key, { headers: { Accept: 'application/json' } }).then(function (res) {
        if (!res.ok) throw new Error('product ' + res.status);
        return res.json();
      }).then(function (product) {
        productData[key] = product;
        return product;
      });
      productCache[key].catch(function () { delete productCache[key]; });
    }
    return productCache[key];
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () { reject(new Error('timeout')); }, ms);
      promise.then(function (v) { clearTimeout(timer); resolve(v); }, function (e) { clearTimeout(timer); reject(e); });
    });
  }

  var sdkPromise = null;

  function loadSdk() {
    if (sdkClient) return Promise.resolve(sdkClient);
    if (sdkPromise) return sdkPromise;
    sdkPromise = initSdk();
    sdkPromise.catch(function () { sdkPromise = null; });
    return sdkPromise;
  }

  function initSdk() {
    var config = window._RestockRocketConfig || {};
    var shop = (window.Shopify && window.Shopify.shop) || config.shop;
    var token = config.storefrontAccessToken;
    if (!shop || !token) return Promise.reject(new Error('no shop or token'));
    var ready = window.Stoq && typeof window.Stoq.init === 'function'
      ? Promise.resolve()
      : new Promise(function (resolve, reject) {
          var script = document.createElement('script');
          script.src = SDK_SRC;
          script.async = true;
          script.onload = resolve;
          script.onerror = function () { reject(new Error('sdk script')); };
          document.head.appendChild(script);
        });
    return withTimeout(ready.then(function () {
      var options = { shop: shop, storefrontToken: token };
      // the storefront's language and market, so STOQ's own copy matches the page
      var locale = config.normalizedLocale || config.locale || (window.Shopify && Shopify.locale);
      if (locale) options.locale = locale;
      if (config.marketId) options.market = String(config.marketId);
      return window.Stoq.init(options);
    }), SDK_TIMEOUT).then(function (client) {
      sdkClient = client;
      return client;
    });
  }

  // The variant that matches the tapped value at its option position and the
  // currently checked values everywhere else.
  function findVariant(product, input) {
    var picker = input.closest('variant-selects, variant-radios') || input.form || document;
    var groups = picker.querySelectorAll('fieldset, .product-form__input');
    var selected = [];
    for (var g = 0; g < groups.length; g++) {
      var checked = groups[g].querySelector('input[type="radio"]:checked');
      if (groups[g].contains(input)) selected.push(input.value);
      else if (checked) selected.push(checked.value);
    }
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      var match = options.length === selected.length;
      for (var j = 0; match && j < options.length; j++) if (options[j] !== selected[j]) match = false;
      if (match) return variants[i];
    }
    if (variants.length && (product.options || []).length === 1) {
      for (var k = 0; k < variants.length; k++) if (variants[k].option1 === input.value) return variants[k];
    }
    return null;
  }

  function setBehind(content, inert) {
    var info = content && content.querySelector('product-info');
    if (!info) return;
    if (inert) info.setAttribute('inert', '');
    else info.removeAttribute('inert');
  }

  function closePanel(content) {
    var panel = content && content.querySelector('.quick-view-notify');
    if (!panel) return;
    panel.parentNode.removeChild(panel);
    setBehind(content, false);
    // hand keyboard focus back to the drawer the theme trapped it in
    var details = content.closest('details');
    if (details && typeof window.trapFocus === 'function') {
      window.trapFocus(details, content.querySelector('variant-selects input:checked + label') || undefined);
    }
  }

  function openPanel(content, product, variant) {
    closePanel(content);
    var panel = document.createElement('div');
    panel.className = 'quick-view-notify';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', product.title + ' · ' + variant.title);
    var back = document.createElement('button');
    back.type = 'button';
    back.className = 'quick-view-notify__back';
    back.textContent = '\u2190 ' + strings.back;
    // The theme closes the drawer on clicks whose target is no longer inside it;
    // keep this click to ourselves and remove the panel after it has finished.
    back.addEventListener('click', function (event) {
      event.stopPropagation();
      setTimeout(function () { closePanel(content); }, 0);
    });
    var heading = document.createElement('p');
    heading.className = 'quick-view-notify__item';
    heading.textContent = product.title + ' · ' + variant.title;
    var form = document.createElement('div');
    form.className = 'quick-view-notify__form';
    form.id = 'QuickViewNotify-' + variant.id;
    form.setAttribute('aria-busy', 'true');
    var loading = document.createElement('p');
    loading.className = 'quick-view-notify__loading';
    loading.textContent = strings.loading;
    form.appendChild(loading);
    panel.appendChild(back);
    panel.appendChild(heading);
    panel.appendChild(form);
    content.appendChild(panel);
    content.scrollTop = 0;
    setBehind(content, true);
    back.focus({ preventScroll: true });
    return form;
  }

  // Fallback when the SDK cannot load or render: STOQ's own modal opens for
  // the same variant. The quick view stays as it is underneath.
  function openModalFallback(product, variant) {
    var api = embed();
    if (api) api.openModal(product, String(variant.id));
  }

  // STOQ's own check accepts addresses without a domain ending ("name@gmail");
  // hold those back here with STOQ's own error line, before its submit runs.
  var EMAIL = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)*\.[^@\s.]{2,}$/;
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.closest || !form.closest('.quick-view-notify__form')) return;
    var input = form.querySelector('input[type="email"]');
    if (!input || EMAIL.test(input.value.trim())) return;
    event.preventDefault();
    event.stopPropagation();
    var error = form.querySelector('.stoq-form-error');
    if (!error) {
      error = document.createElement('p');
      error.className = 'stoq-form-error';
      error.setAttribute('aria-live', 'polite');
      input.parentNode.insertAdjacentElement('afterend', error);
    }
    error.textContent = strings.invalidEmail;
    error.style.display = '';
    input.classList.add('stoq-form-input--error');
    input.focus({ preventScroll: true });
  }, true);

  // Every loaded quick view renders its inputs with ids from the same section
  // id, so look the input up inside the quick view that holds the label.
  function inputFor(label) {
    var scope = label.closest('.quick-view__content') || document;
    var id = label.getAttribute('for');
    return id ? scope.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id)) : null;
  }

  function soldOutLabel(target) {
    var label = target && target.closest && target.closest('.quick-view__content label[for]');
    if (!label) return null;
    var input = inputFor(label);
    return input && input.type === 'radio' && input.hasAttribute('data-crossout') ? label : null;
  }

  // The theme closes the quick view when focus leaves it. A sold-out size is a
  // disabled input, so a tap on it would drop focus to the page; give the label
  // focus first so focus stays inside the drawer.
  document.addEventListener('pointerdown', function (event) {
    var label = soldOutLabel(event.target);
    if (!label) return;
    if (!label.hasAttribute('tabindex')) label.setAttribute('tabindex', '-1');
    label.focus({ preventScroll: true });
  }, true);

  document.addEventListener('click', function (event) {
    var label = event.target.closest && event.target.closest('.quick-view__content label[for]');
    if (!label) return;
    var input = inputFor(label);
    if (!input || input.type !== 'radio' || !input.hasAttribute('data-crossout')) return;
    if (!embed()) return;
    event.preventDefault();
    var content = label.closest('.quick-view__content');
    var info = input.closest('product-info');
    var url = input.getAttribute('data-product-url') || (info && info.getAttribute('data-url')) || '';
    loadProduct(url).then(function (product) {
      var variant = findVariant(product, input);
      if (!variant || variant.available) return;
      var form = openPanel(content, product, variant);
      return loadSdk().then(function (client) {
        if (!client.signupsEnabled) throw new Error('signups disabled');
        if (!form.isConnected) return;
        var render = typeof client.openInlineForm === 'function'
          ? client.openInlineForm.bind(client)
          : window.Stoq.openInlineForm.bind(window.Stoq);
        var loadingLine = form.querySelector('.quick-view-notify__loading');
        // resolve as soon as STOQ's form is in the container (no fixed wait)
        var rendered = new Promise(function (resolve, reject) {
          var done = function () {
            if (!form.querySelector('input, button')) return false;
            watch.disconnect();
            resolve();
            return true;
          };
          var watch = new MutationObserver(done);
          watch.observe(form, { childList: true, subtree: true });
          setTimeout(function () { if (!done()) { watch.disconnect(); reject(new Error('empty form')); } }, SDK_TIMEOUT);
        });
        // productId is required: without it STOQ's submit stops before sending
        return Promise.resolve(render({
          container: '#' + form.id,
          variantId: variant.id,
          productId: product.id,
          productTitle: product.title,
          variantTitle: variant.title
        })).then(function () {
          return rendered;
        }).then(function () {
          if (!form.isConnected) return;
          if (loadingLine && loadingLine.parentNode) loadingLine.parentNode.removeChild(loadingLine);
          form.removeAttribute('aria-busy');
          var panel = form.closest('.quick-view-notify');
          if (panel && typeof window.trapFocus === 'function') window.trapFocus(panel, panel.querySelector('.quick-view-notify__back'));
        });
      }).catch(function () {
        closePanel(content);
        openModalFallback(product, variant);
      });
    }).catch(function () {});
  });

  // Pre-order variants: STOQ already attaches its "Preorder" selling plan to the
  // cart line from the quick view too; only the button wording is the theme's.
  // Label it the way the product page does while a pre-order variant is selected.
  function preorderLabel(planId) {
    var config = window._RestockRocketConfig || {};
    var plans = config.sellingPlans || [];
    var locale = (config.normalizedLocale || config.locale || '').split('-')[0];
    for (var i = 0; i < plans.length; i++) {
      if (String(plans[i].shopify_selling_plan_id || plans[i].selling_plan_id || plans[i].id) !== String(planId)) continue;
      var t = plans[i].translations && plans[i].translations[locale];
      return (t && t.preorder_button_text) || plans[i].preorder_button_text;
    }
    if (plans.length && plans[0].preorder_button_text) return plans[0].preorder_button_text;
    return (window.theme && theme.variantStrings && theme.variantStrings.preOrder) || 'Pre-order';
  }

  // the id of the pre-order selling plan on this variant, or null
  function preorderPlan(product, variantId) {
    var groups = product.selling_plan_groups || [];
    var preorderPlans = {};
    for (var g = 0; g < groups.length; g++) {
      if (!/pre-?order/i.test(groups[g].name || '')) continue;
      var plans = groups[g].selling_plans || [];
      for (var p = 0; p < plans.length; p++) preorderPlans[plans[p].id] = true;
    }
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) !== String(variantId)) continue;
      var allocations = variants[i].selling_plan_allocations || [];
      for (var a = 0; a < allocations.length; a++) if (preorderPlans[allocations[a].selling_plan_id]) return allocations[a].selling_plan_id;
    }
    return null;
  }

  function labelPreorder(content) {
    var info = content.querySelector('product-info');
    var button = content.querySelector('.product-form__submit');
    // the variant id of the buy form itself (the price block renders an
    // installments form earlier in the drawer that is not kept in sync)
    var buyForm = button && (button.form || button.closest('form'));
    var idInput = buyForm && buyForm.querySelector('input[name="id"]');
    if (!info || !button || !idInput || button.disabled) return;
    var apply = function (product) {
      var plan = preorderPlan(product, idInput.value);
      if (!plan) return;
      var label = preorderLabel(plan);
      var span = button.querySelector('span') || button;
      if (span.textContent.trim() !== label) span.textContent = label;
    };
    var known = productData[productJsUrl(info.getAttribute('data-url'))];
    if (known) { apply(known); return; }
    loadProduct(info.getAttribute('data-url')).then(apply).catch(function () {});
  }

  // Watch only the open quick view: the theme announces when its content has
  // loaded (quickview:loaded) and when it closes (quickview:close); variant
  // changes inside it re-render the button.
  var watched = null;
  var observer = new MutationObserver(function () {
    if (watched) labelPreorder(watched);
  });
  // The theme fires quickview:loaded when any card's content arrives, not only
  // for the one that is open; attach to the content inside the open <details>.
  function watchOpenQuickView() {
    var content = document.querySelector('quick-view-drawer details[open] .quick-view__content');
    if (!content || !content.querySelector('product-info') || content === watched) return;
    observer.disconnect();
    watched = content;
    observer.observe(watched, { childList: true, subtree: true, characterData: true });
    labelPreorder(watched);
    // a product with a sold-out size: get STOQ ready before the tap
    if (watched.querySelector('input[type="radio"][data-crossout]') && embed()) loadSdk().catch(function () {});
  }
  document.addEventListener('quickview:loaded', watchOpenQuickView);
  document.addEventListener('quickview:open', function () { setTimeout(watchOpenQuickView, 0); });
  document.addEventListener('quickview:close', function () {
    observer.disconnect();
    watched = null;
  });
})();
